import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { cx } from "../lib/cx";
import { Alert, ArrowDown, ArrowUp, ArrowsUpDown, Check, Close, Columns, InboxEmpty, RotateCcw } from "../icons/icons";
import { type DataTableStrings, useUiStrings } from "../strings/ui-strings";
import { ListPagination, Pagination } from "./pagination";
import type {
	BulkAction,
	BulkContext,
	ColumnDef,
	ColumnVisibilityProps,
	DataTableMessages,
	EmptyState,
	ErrorState,
	PaginationProps,
	SelectionProps,
	SortState,
} from "./types";

/**
 * DataTable — the platform's list view. daisyUI `table` underneath, restyled:
 * a quiet uppercase header band, hairline rows, a tinted row when selected,
 * and a toolbar that turns into the bulk-action bar the moment anything is
 * ticked (the Gmail pattern — the actions appear where the filters were, so
 * the eye does not have to travel).
 *
 * It owns NO data and NO URL state. The app fetches the page, decides the
 * sort, stores the selection (see `useRowSelection`) and passes everything
 * in, so the same component serves a TanStack-Router app today and whatever
 * OVR / TSR / MSES use tomorrow.
 *
 * Every size is rem (CLAUDE.md § Conventions); hairlines are the only px.
 */
export interface DataTableProps<T, K extends string = string> {
	columns: ReadonlyArray<ColumnDef<T, K>>;
	rows: ReadonlyArray<T>;
	/** Stable id per row — used for selection and React keys. */
	rowKey: (row: T) => string;

	/** Fetching. With rows present the table dims and shows a progress line; with none, skeleton rows. */
	loading?: boolean;
	error?: ErrorState | null;
	empty?: EmptyState;

	/** Header band above the toolbar. */
	title?: ReactNode;
	description?: ReactNode;
	/** Left side of the toolbar: search box, filter selects. Replaced by the bulk bar while rows are selected. */
	toolbar?: ReactNode;
	/** Right side of the toolbar: primary actions ("New user"). Stays visible during selection. */
	toolbarEnd?: ReactNode;

	selection?: SelectionProps<T>;
	/** Buttons for the bulk bar, or a render function for anything richer. Needs `selection`. */
	bulkActions?: ReadonlyArray<BulkAction> | ((ctx: BulkContext) => ReactNode);

	sort?: {
		state: SortState<K> | null;
		onChange: (next: SortState<K> | null) => void;
	};
	pagination?: PaginationProps;
	/**
	 * `list` only: a pager of the app's own (a `KeysetPager`) drawn where the
	 * numbered pager would be, above and below the card. Ignored when
	 * `pagination` is set. Return null to leave a position empty.
	 */
	pager?: (position: "top" | "bottom") => ReactNode;

	/** Trailing per-row cell — use `IconButton`s (eye = view). Not part of `columns` so it never sorts or hides. */
	rowActions?: (row: T) => ReactNode;
	/** Header of the row-actions column. Default from the provider (platform: "Action"). */
	rowActionsHeader?: ReactNode;
	/**
	 * Lets the user choose which columns to show (a "Columns" picker on the
	 * toolbar). Pair with `useColumnVisibility(storageKey)` to persist it.
	 */
	columnVisibility?: ColumnVisibilityProps;
	onRowClick?: (row: T) => void;
	rowClassName?: (row: T) => string | undefined;

	/**
	 * Per-table overrides for the table's own chrome. Usually unnecessary: the
	 * defaults come from `<UiStringsProvider>` (English without one).
	 */
	messages?: DataTableMessages;

	/** `sm` is the dense desk view; `md` the default. */
	size?: "sm" | "md";
	/**
	 * `list` — the designer's settings-list look (GMS prototype `.tbl` /
	 * `.pgr`): soft uppercase header band, compact 13px rows, navy-soft hover
	 * on row actions, and the "Showing X to Y of N" pager with numbered pages
	 * ABOVE and BELOW the card (the Columns picker rides on the top pager
	 * row). Needs the app theme's `gms-*` colours (see apps/gms styles.css).
	 * `default` is unchanged, so existing tables (users, OVR) do not move.
	 */
	variant?: "default" | "list";
	/** Skeleton rows while loading with no data. Defaults to the page size, capped at 10. */
	skeletonRows?: number;
	className?: string;
}

const ALIGN = { left: "text-left", center: "text-center", right: "text-right" } as const;

export function DataTable<T, K extends string = string>({
	columns: allColumns,
	rows,
	rowKey,
	loading = false,
	error,
	empty,
	title,
	description,
	toolbar,
	toolbarEnd,
	selection,
	bulkActions,
	sort,
	pagination,
	pager,
	rowActions,
	rowActionsHeader,
	columnVisibility,
	onRowClick,
	rowClassName,
	messages,
	size = "md",
	skeletonRows,
	className,
	variant = "default",
}: DataTableProps<T, K>) {
	const isList = variant === "list";
	const { dataTable: strings, formatNumber } = useUiStrings();
	const m = useMemo(() => mergeMessages(strings, messages), [strings, messages]);
	const hiddenCols = columnVisibility?.hidden;
	const columns = useMemo(
		() => (hiddenCols ? allColumns.filter((c) => c.lockVisible || !hiddenCols.has(c.key)) : allColumns),
		[allColumns, hiddenCols],
	);
	const keys = useMemo(() => rows.map(rowKey), [rows, rowKey]);
	const selectable = useMemo(
		() => rows.map((r) => (selection?.isSelectable ? selection.isSelectable(r) : true)),
		[rows, selection],
	);

	/* ---------- selection ---------- */
	const selected = selection?.selected;
	const pageSelectableKeys = keys.filter((_, i) => selectable[i]);
	const pageSelectedCount = selected ? pageSelectableKeys.filter((k) => selected.has(k)).length : 0;
	const allOnPage = pageSelectableKeys.length > 0 && pageSelectedCount === pageSelectableKeys.length;
	const someOnPage = pageSelectedCount > 0 && !allOnPage;
	const offPageCount = selected ? selected.size - pageSelectedCount : 0;

	const headerCb = useRef<HTMLInputElement>(null);
	useEffect(() => {
		if (headerCb.current) headerCb.current.indeterminate = someOnPage;
	}, [someOnPage]);

	// Shift-click range selection, anchored at the last plain click on this page.
	const anchor = useRef<number | null>(null);

	const togglePage = () => {
		if (!selection || !selected) return;
		const next = new Set(selected);
		if (allOnPage) for (const k of pageSelectableKeys) next.delete(k);
		else for (const k of pageSelectableKeys) next.add(k);
		selection.onChange(next);
	};

	const toggleRow = (index: number, shiftKey: boolean) => {
		if (!selection || !selected || !selectable[index]) return;
		const next = new Set(selected);
		const key = keys[index]!;
		if (shiftKey && anchor.current !== null && anchor.current !== index) {
			const [lo, hi] = anchor.current < index ? [anchor.current, index] : [index, anchor.current];
			const turnOn = !selected.has(key);
			for (let i = lo; i <= hi; i++) {
				if (!selectable[i]) continue;
				if (turnOn) next.add(keys[i]!);
				else next.delete(keys[i]!);
			}
		} else {
			if (next.has(key)) next.delete(key);
			else next.add(key);
			anchor.current = index;
		}
		selection.onChange(next);
	};

	const clearSelection = () => selection?.onChange(new Set());

	/* ---------- sort ---------- */
	const cycleSort = (key: K) => {
		if (!sort) return;
		const cur = sort.state;
		if (!cur || cur.key !== key) sort.onChange({ key, dir: "asc" });
		else if (cur.dir === "asc") sort.onChange({ key, dir: "desc" });
		else sort.onChange(null);
	};

	/* ---------- layout ---------- */
	const dense = size === "sm";
	// list: .tbl th/td — 10px 12px everywhere.
	const cellPad = isList ? "px-[0.75rem] py-[0.625rem]" : dense ? "px-[0.75rem] py-[0.5rem]" : "px-[1rem] py-[0.75rem]";
	const firstPad = isList ? "pl-[0.875rem]" : dense ? "pl-[1rem]" : "pl-[1.25rem]";
	const lastPad = isList ? "pr-[0.875rem]" : dense ? "pr-[1rem]" : "pr-[1.25rem]";
	// Both variants use the designer's `.tbl` head: 11.5px semibold uppercase ink-2 on --soft-2.
	const thText = "text-[0.71875rem] font-semibold tracking-[0.04em] whitespace-nowrap text-gms-ink-2 uppercase";
	const rowLine = "border-gms-line";
	// In `list` the Columns picker lives on the pager row above the card, not in the card's toolbar.
	const pickerInToolbar = !!columnVisibility && !isList;
	const colCount = columns.length + (selection ? 1 : 0) + (rowActions ? 1 : 0);
	const showSkeleton = loading && rows.length === 0 && !error;
	const showEmpty = !loading && !error && rows.length === 0;
	const skeletonCount = Math.min(10, skeletonRows ?? pagination?.pageSize ?? 8);
	const hasSelection = !!selected && selected.size > 0;

	const card = (
		<section
			className={cx(
				// No overflow-hidden on the card: the Columns picker (and any row menu)
				// must be able to pop out of it. Corners are rounded on the first/last
				// child instead; the scroller clips its own content.
				"relative flex flex-col bg-base-100 [&>*:first-child]:rounded-t-[inherit] [&>*:last-child]:rounded-b-[inherit]",
				isList ? "rounded-[0.625rem] border border-gms-line" : "rounded-box border border-base-300",
				!isList && className,
			)}
			aria-busy={loading || undefined}
		>
			{/* Head band */}
			{(title || description) && (
				<header className="flex flex-col gap-[0.125rem] border-b border-base-300 px-[1.25rem] pt-[1rem] pb-[0.875rem]">
					{title && <h2 className="text-[1.0625rem] leading-tight font-semibold tracking-tight">{title}</h2>}
					{description && <p className="text-[0.8125rem] text-base-content/60">{description}</p>}
				</header>
			)}

			{/* Toolbar ⇄ bulk bar */}
			{(toolbar || toolbarEnd || pickerInToolbar || (hasSelection && bulkActions)) && (
				<div
					className={cx(
						"flex flex-wrap items-center gap-x-[0.75rem] gap-y-[0.5rem] border-b transition-colors duration-150",
						isList
							? // Her desktop `.bulk`: a navy-ink band, white text, small white buttons.
								cx("min-h-[3rem] px-[0.75rem] py-[0.5rem] text-[0.8125rem]", hasSelection ? "border-gms-navy-ink bg-gms-navy-ink text-white" : "border-gms-line")
							: cx("min-h-[3.25rem] px-[1rem] py-[0.5rem]", hasSelection ? "border-primary/20 bg-primary/5" : "border-base-300"),
					)}
				>
					{hasSelection && selection ? (
						<BulkBar
							count={selected.size}
							offPageCount={offPageCount}
							ids={[...selected]}
							clear={clearSelection}
							actions={bulkActions}
							messages={m}
							formatNumber={formatNumber}
							dark={isList}
						/>
					) : (
						<div className="flex min-w-0 flex-1 flex-wrap items-center gap-[0.5rem]">{toolbar}</div>
					)}
					{(toolbarEnd || pickerInToolbar) && (
						<div className="ml-auto flex shrink-0 items-center gap-[0.5rem]">
							{toolbarEnd}
							{pickerInToolbar && columnVisibility && (
								<ColumnPicker columns={allColumns} visibility={columnVisibility} messages={m} formatNumber={formatNumber} />
							)}
						</div>
					)}
				</div>
			)}

			{/* Progress hairline while refetching over existing rows */}
			{loading && rows.length > 0 && (
				<div aria-hidden className="relative h-px w-full overflow-hidden bg-base-300">
					<div className="dt-progress absolute inset-y-0 w-1/3 bg-primary" />
				</div>
			)}

			<div className="overflow-x-auto">
				<table
					className={cx(
						"table w-full",
						"text-[0.8125rem]",
						loading && rows.length > 0 && "opacity-60 transition-opacity",
					)}
				>
					<thead>
						<tr className="border-b border-gms-line bg-gms-soft-2">
							{selection && (
								<th scope="col" className={cx("w-[2.5rem]", cellPad, firstPad, "pr-0")}>
									<input
										ref={headerCb}
										type="checkbox"
										className="checkbox checkbox-sm rounded-[0.25rem] border-base-content/30 checked:border-primary checked:bg-primary checked:text-primary-content indeterminate:border-primary indeterminate:bg-primary indeterminate:text-primary-content"
										checked={allOnPage}
										onChange={togglePage}
										disabled={pageSelectableKeys.length === 0}
										aria-label={allOnPage ? m.deselectAllRows : m.selectAllRows}
									/>
								</th>
							)}
							{columns.map((col, ci) => {
								const isSorted = sort?.state?.key === col.key;
								const dir = isSorted ? sort?.state?.dir : undefined;
								const align = col.numeric ? "right" : (col.align ?? "left");
								const label = (
									<span className="inline-flex items-center gap-[0.25rem]">
										{col.header}
										{col.sortable && sort && (
											<span className={cx("inline-flex", isSorted ? (isList ? "text-gms-navy" : "text-primary") : "text-base-content/35")}>
												{dir === "asc" ? (
													<ArrowUp className="size-[0.875rem]" />
												) : dir === "desc" ? (
													<ArrowDown className="size-[0.875rem]" />
												) : (
													<ArrowsUpDown className="size-[0.875rem]" />
												)}
											</span>
										)}
									</span>
								);
								return (
									<th
										key={col.key}
										scope="col"
										style={{
											...(col.width ? { width: col.width } : null),
											...(col.minWidth ? { minWidth: col.minWidth } : null),
										}}
										aria-sort={isSorted ? (dir === "asc" ? "ascending" : "descending") : undefined}
										className={cx(
											thText,
											cellPad,
											ci === 0 && !selection && firstPad,
											ci === columns.length - 1 && !rowActions && lastPad,
											ALIGN[align],
											col.headerClassName,
										)}
									>
										{col.sortable && sort ? (
											<button
												type="button"
												onClick={() => cycleSort(col.key)}
												className={cx(
													"-mx-[0.375rem] inline-flex cursor-pointer items-center rounded-[0.25rem] px-[0.375rem] py-[0.125rem] uppercase outline-none transition-colors hover:text-base-content focus-visible:ring-2 focus-visible:ring-primary/50",
													isSorted && (isList ? "text-gms-navy-ink" : "text-base-content"),
												)}
											>
												{label}
											</button>
										) : (
											label
										)}
									</th>
								);
							})}
							{rowActions && (
								<th
									scope="col"
									className={cx(
										"w-[1%]",
										thText,
										cellPad,
										lastPad,
										"text-center",
									)}
								>
									{rowActionsHeader ?? strings.actionHeader}
								</th>
							)}
						</tr>
					</thead>

					<tbody>
						{showSkeleton &&
							Array.from({ length: skeletonCount }, (_, i) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: placeholders
								<tr key={i} className={cx("border-b last:border-0", rowLine)}>
									{selection && (
										<td className={cx(cellPad, firstPad, "pr-0")}>
											<div className="skeleton size-[1.25rem] rounded-[0.25rem]" />
										</td>
									)}
									{columns.map((col, ci) => (
										<td
											key={col.key}
											className={cx(
												cellPad,
												ci === 0 && !selection && firstPad,
												)}
										>
											<div
												className="skeleton h-[0.875rem] rounded-[0.25rem]"
												style={{ width: `${45 + ((i * 17 + ci * 29) % 45)}%` }}
											/>
										</td>
									))}
									{rowActions && <td className={cx(cellPad, lastPad)} />}
								</tr>
							))}

						{error && isList && (
							<tr>
								<td colSpan={colCount} className="px-[1rem] py-[2.25rem]">
									<div className="mx-auto grid max-w-[30rem] justify-items-center gap-[0.5rem] text-center text-[0.84375rem] text-gms-ink-2">
										<Alert aria-hidden className="size-[2rem] text-gms-bad" />
										<b className="text-[0.9375rem] font-semibold text-gms-navy-ink">{m.errorTitle}</b>
										<span>{error.message}</span>
										{error.onRetry && (
											<button type="button" className="btn btn-line btn-sm mt-[0.25rem]" onClick={error.onRetry}>
												{m.retry}
											</button>
										)}
									</div>
								</td>
							</tr>
						)}

						{showEmpty && isList && (
							<tr>
								{/* Her `.empty`: a 32px ink-3 icon, a 15px title, one line of why and what next. */}
								<td colSpan={colCount} className="px-[1rem] py-[2.25rem]">
									<div className="mx-auto grid max-w-[30rem] justify-items-center gap-[0.5rem] text-center text-[0.84375rem] text-gms-ink-2">
										<InboxEmpty aria-hidden className="size-[2rem] text-gms-ink-3" />
										<b className="text-[0.9375rem] font-semibold text-gms-navy-ink">{empty?.title ?? m.emptyTitle}</b>
										{empty?.description && <span>{empty.description}</span>}
										{empty?.action && <div className="mt-[0.25rem]">{empty.action}</div>}
									</div>
								</td>
							</tr>
						)}

						{error && !isList && (
							<tr>
								<td colSpan={colCount} className="px-[1.25rem] py-[3rem]">
									<div className="mx-auto flex max-w-[26rem] flex-col items-center gap-[0.5rem] text-center">
										<span className="flex size-[2.5rem] items-center justify-center rounded-full bg-error/10 text-error">
											<Alert className="size-[1.25rem]" />
										</span>
										<p className="font-medium">{m.errorTitle}</p>
										<p className="text-[0.8125rem] text-base-content/60">{error.message}</p>
										{error.onRetry && (
											<button type="button" className="btn btn-sm btn-outline mt-[0.5rem]" onClick={error.onRetry}>
												{m.retry}
											</button>
										)}
									</div>
								</td>
							</tr>
						)}

						{showEmpty && !isList && (
							<tr>
								<td colSpan={colCount} className="px-[1.25rem] py-[3.5rem]">
									<div className="mx-auto flex max-w-[26rem] flex-col items-center gap-[0.5rem] text-center">
										<span className="flex size-[2.75rem] items-center justify-center rounded-full bg-base-200 text-base-content/50">
											<InboxEmpty className="size-[1.375rem]" />
										</span>
										<p className="font-medium">{empty?.title ?? m.emptyTitle}</p>
										{empty?.description && (
											<p className="text-[0.8125rem] text-base-content/60">{empty.description}</p>
										)}
										{empty?.action && <div className="mt-[0.5rem]">{empty.action}</div>}
									</div>
								</td>
							</tr>
						)}

						{!error &&
							rows.map((row, ri) => {
								const key = keys[ri]!;
								const isSel = !!selected?.has(key);
								const canSel = selectable[ri]!;
								return (
									<tr
										key={key}
										aria-selected={selection ? isSel : undefined}
										onClick={onRowClick ? () => onRowClick(row) : undefined}
										className={cx(
											"group border-b transition-colors last:border-0",
											rowLine,
											isSel ? "bg-[#f2f3fc]" : "hover:bg-gms-soft",
											onRowClick && "cursor-pointer",
											rowClassName?.(row),
										)}
									>
										{selection && (
											<td className={cx(cellPad, firstPad, "pr-0 align-middle")} onClick={(e) => e.stopPropagation()}>
												<input
													type="checkbox"
													className="checkbox checkbox-sm rounded-[0.25rem] border-base-content/30 checked:border-primary checked:bg-primary checked:text-primary-content disabled:opacity-30"
													checked={isSel}
													disabled={!canSel}
													onChange={() => {}}
													onClick={(e) => toggleRow(ri, e.shiftKey)}
													aria-label={m.selectRow(ri + 1)}
												/>
											</td>
										)}
										{columns.map((col, ci) => {
											const align = col.numeric ? "right" : (col.align ?? "left");
											return (
												<td
													key={col.key}
													style={col.minWidth ? { minWidth: col.minWidth } : undefined}
													className={cx(
														"align-middle",
														cellPad,
														ci === 0 && !selection && firstPad,
														ci === columns.length - 1 && !rowActions && lastPad,
														ALIGN[align],
														col.numeric && "tabular-nums",
																	col.className,
													)}
												>
													{col.cell(row, ri)}
												</td>
											);
										})}
										{rowActions && (
											<td
												className={cx("w-[1%] align-middle whitespace-nowrap", cellPad, lastPad, "text-center")}
												onClick={(e) => e.stopPropagation()}
											>
												<div className="inline-flex items-center justify-center gap-[0.125rem]">{rowActions(row)}</div>
											</td>
										)}
									</tr>
								);
							})}
					</tbody>
				</table>
			</div>

			{pagination && !isList && (
				<footer className="border-t border-base-300 px-[1rem] py-[0.625rem]">
					<Pagination {...pagination} />
				</footer>
			)}

			{/* The refetch hairline's sweep. Scoped here so consumers need no extra CSS. */}
			<style>{`@keyframes dt-sweep{from{transform:translateX(-100%)}to{transform:translateX(400%)}}.dt-progress{animation:dt-sweep 1.1s cubic-bezier(.4,0,.2,1) infinite}
`}</style>
		</section>
	);

	if (!isList) return card;

	// list: pager (+ Columns) above the card, pager again below it — like the prototype's .pgr / .pgr--b.
	return (
		<div className={cx("flex min-w-0 flex-col gap-[0.875rem]", className)}>
			{(pagination || pager || columnVisibility) && (
				<div className="flex flex-wrap items-center gap-x-[0.875rem] gap-y-[0.5rem]">
					{pagination ? <ListPagination {...pagination} className="min-w-0 flex-1" /> : pager && <div className="min-w-0 flex-1">{pager("top")}</div>}
					{columnVisibility && (
						<div className="ml-auto">
							<ColumnPicker columns={allColumns} visibility={columnVisibility} messages={m} formatNumber={formatNumber} list />
						</div>
					)}
				</div>
			)}
			{card}
			{pagination ? (
				pagination.total > 0 && <ListPagination {...pagination} position="bottom" className="mt-[0.375rem]" />
			) : (
				pager && <div className="mt-[0.375rem]">{pager("bottom")}</div>
			)}
		</div>
	);
}

/* ------------------------------------------------------------------ */

/** The provider's strings with this table's `messages` laid over them. */
type ResolvedMessages = Omit<
	DataTableStrings,
	"actionHeader" | "errorTitle" | "retry" | "emptyTitle" | "clearSelection" | "columns" | "showColumns" | "columnAlways" | "showAllColumns"
> & {
	errorTitle: ReactNode;
	retry: ReactNode;
	emptyTitle: ReactNode;
	clearSelection: ReactNode;
	columns: ReactNode;
	showColumns: ReactNode;
	columnAlways: ReactNode;
	showAllColumns: ReactNode;
};

function mergeMessages(strings: DataTableStrings, messages: DataTableMessages | undefined): ResolvedMessages {
	return {
		errorTitle: messages?.errorTitle ?? strings.errorTitle,
		retry: messages?.retry ?? strings.retry,
		emptyTitle: messages?.emptyTitle ?? strings.emptyTitle,
		selected: messages?.selected ?? strings.selected,
		onOtherPages: messages?.onOtherPages ?? strings.onOtherPages,
		clearSelection: messages?.clearSelection ?? strings.clearSelection,
		columns: messages?.columns ?? strings.columns,
		showColumns: messages?.showColumns ?? strings.showColumns,
		columnAlways: messages?.columnAlways ?? strings.columnAlways,
		showAllColumns: messages?.showAllColumns ?? strings.showAllColumns,
		hiddenCount: messages?.hiddenCount ?? strings.hiddenCount,
		selectRow: messages?.selectRow ?? strings.selectRow,
		selectAllRows: messages?.selectAllRows ?? strings.selectAllRows,
		deselectAllRows: messages?.deselectAllRows ?? strings.deselectAllRows,
	};
}

const BULK_VARIANT: Record<NonNullable<BulkAction["variant"]>, string> = {
	default: "btn-ghost border-base-300 bg-base-100 hover:bg-base-200",
	primary: "btn-primary",
	danger: "btn-ghost border-error/30 bg-base-100 text-error hover:bg-error/10 hover:border-error/50",
};

// Her `.bulk .btn`: 34px, 13px, white on the navy-ink band; danger is outlined pink; "clear" is outlined white.
const BULK_DARK_BASE =
	"inline-flex min-h-[2.125rem] cursor-pointer items-center gap-[0.375rem] rounded-[0.5rem] border px-[0.75rem] text-[0.8125rem] font-semibold whitespace-nowrap outline-none transition-colors focus-visible:ring-2 focus-visible:ring-white/60 disabled:cursor-not-allowed disabled:opacity-50 [&>svg]:size-[0.875rem]";
const BULK_DARK: Record<NonNullable<BulkAction["variant"]>, string> = {
	default: "border-white bg-white text-gms-navy-ink hover:enabled:bg-gms-navy-soft",
	primary: "border-white bg-white text-gms-navy-ink hover:enabled:bg-gms-navy-soft",
	danger: "border-[#ffb4b0]/50 bg-transparent text-[#ffb4b0] hover:enabled:bg-white/10",
};

function BulkBar({
	count,
	offPageCount,
	ids,
	clear,
	actions,
	messages,
	formatNumber,
	dark = false,
}: {
	count: number;
	offPageCount: number;
	ids: string[];
	clear: () => void;
	actions: DataTableProps<unknown>["bulkActions"];
	messages: ResolvedMessages;
	formatNumber: (n: number) => string;
	/** On her navy-ink `.bulk` band (DataTable `variant="list"`). */
	dark?: boolean;
}) {
	const [busy, setBusy] = useState<string | null>(null);

	const run = async (a: BulkAction) => {
		setBusy(a.key);
		try {
			await a.onClick(ids);
		} finally {
			setBusy(null);
		}
	};

	if (dark)
		return (
			<div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-[0.5rem] gap-y-[0.5rem]">
				<b className="mr-auto font-semibold tabular-nums">
					{formatNumber(count)} {messages.selected}
					{offPageCount > 0 && (
						<span className="font-normal text-white/70">
							{" · "}
							{messages.onOtherPages(offPageCount)}
						</span>
					)}
				</b>
				<div className="flex flex-wrap items-center gap-[0.5rem]">
					{typeof actions === "function"
						? actions({ ids, count, clear })
						: actions?.map((a) => (
								<button
									key={a.key}
									type="button"
									title={a.title}
									disabled={a.disabled || busy !== null}
									onClick={() => void run(a)}
									className={cx(BULK_DARK_BASE, BULK_DARK[a.variant ?? "default"])}
								>
									{busy === a.key ? <span className="loading loading-spinner loading-xs" /> : a.icon}
									{a.label}
								</button>
							))}
					<button type="button" onClick={clear} className={cx(BULK_DARK_BASE, "border-white/40 bg-transparent text-white hover:enabled:bg-white/10")}>
						<Close className="size-[0.875rem]" />
						{messages.clearSelection}
					</button>
				</div>
			</div>
		);

	return (
		<div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-[0.75rem] gap-y-[0.5rem]">
			<div className="flex items-center gap-[0.5rem]">
				<span className="inline-flex h-[1.5rem] min-w-[1.5rem] items-center justify-center rounded-full bg-primary px-[0.5rem] text-[0.75rem] font-bold text-primary-content tabular-nums">
					{formatNumber(count)}
				</span>
				<span className="text-[0.8125rem] font-medium">
					{messages.selected}
					{offPageCount > 0 && (
						<span className="font-normal text-base-content/60">
							{" · "}
							{messages.onOtherPages(offPageCount)}
						</span>
					)}
				</span>
				<button
					type="button"
					onClick={clear}
					className="btn btn-ghost btn-xs gap-[0.25rem] text-base-content/60 hover:text-base-content"
				>
					<Close className="size-[0.875rem]" />
					{messages.clearSelection}
				</button>
			</div>

			<span aria-hidden className="hidden h-[1.25rem] w-px bg-base-content/15 sm:block" />

			<div className="flex flex-wrap items-center gap-[0.5rem]">
				{typeof actions === "function"
					? actions({ ids, count, clear })
					: actions?.map((a) => (
							<button
								key={a.key}
								type="button"
								title={a.title}
								disabled={a.disabled || busy !== null}
								onClick={() => void run(a)}
								className={cx("btn btn-sm gap-[0.375rem]", BULK_VARIANT[a.variant ?? "default"])}
							>
								{busy === a.key ? <span className="loading loading-spinner loading-xs" /> : a.icon}
								{a.label}
							</button>
						))}
			</div>
		</div>
	);
}

/* ------------------------------------------------------------------ */

function ColumnPicker<T, K extends string>({
	columns,
	visibility,
	messages,
	formatNumber,
	list = false,
}: {
	columns: ReadonlyArray<ColumnDef<T, K>>;
	visibility: ColumnVisibilityProps;
	messages: ResolvedMessages;
	formatNumber: (n: number) => string;
	/** The designer's look: a 36px white `.btn--line` beside her pager. */
	list?: boolean;
}) {
	const [open, setOpen] = useState(false);
	const root = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!open) return;
		const onDown = (e: MouseEvent) => {
			if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
		};
		const onKey = (e: globalThis.KeyboardEvent) => {
			if (e.key === "Escape") setOpen(false);
		};
		document.addEventListener("mousedown", onDown);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onDown);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);

	const hiddenCount = columns.filter((c) => !c.lockVisible && visibility.hidden.has(c.key)).length;
	const toggle = (key: string) => {
		const next = new Set(visibility.hidden);
		if (next.has(key)) next.delete(key);
		else next.add(key);
		visibility.onChange(next);
	};

	return (
		<div ref={root} className="relative">
			<button
				type="button"
				aria-haspopup="true"
				aria-expanded={open}
				onClick={() => setOpen((o) => !o)}
				className={
					list
						? "inline-flex h-[2.25rem] cursor-pointer items-center gap-[0.375rem] rounded-[0.5rem] border border-gms-line-2 bg-base-100 px-[0.75rem] text-[0.8125rem] font-medium text-gms-navy-ink outline-none transition-colors hover:border-gms-navy focus-visible:ring-2 focus-visible:ring-gms-navy/40 [&>svg]:text-gms-ink-2"
						: "btn btn-ghost btn-sm gap-[0.375rem] border-base-300 bg-base-100 font-medium text-base-content/80 hover:bg-base-200"
				}
			>
				<Columns className="size-[1rem]" />
				{messages.columns}
				{hiddenCount > 0 && (
					<span
						className="badge badge-xs badge-neutral tabular-nums"
						title={messages.hiddenCount(hiddenCount)}
					>
						{formatNumber(hiddenCount)}
					</span>
				)}
			</button>
			{open && (
				<div className="absolute top-full right-0 z-30 mt-[0.25rem] w-[14rem] overflow-hidden rounded-box border border-base-300 bg-base-100 shadow-[0_8px_24px_rgba(16,24,40,0.12)]">
					<p className="px-[0.75rem] pt-[0.625rem] pb-[0.25rem] text-[0.625rem] font-semibold tracking-[0.08em] text-base-content/50 uppercase">
						{messages.showColumns}
					</p>
					<ul className="max-h-[18rem] overflow-y-auto p-[0.25rem] [scrollbar-width:thin]">
						{columns.map((c) => {
							const on = c.lockVisible || !visibility.hidden.has(c.key);
							return (
								<li key={c.key}>
									<button
										type="button"
										role="menuitemcheckbox"
										aria-checked={on}
										disabled={c.lockVisible}
										onClick={() => toggle(c.key)}
										className={cx(
											"flex w-full items-center gap-[0.5rem] rounded-[0.375rem] px-[0.5rem] py-[0.375rem] text-left text-[0.8125rem] hover:bg-base-200",
											c.lockVisible && "cursor-default opacity-60 hover:bg-transparent",
										)}
									>
										<span
											aria-hidden
											className={cx(
												"flex size-[1rem] shrink-0 items-center justify-center rounded-[0.25rem] border",
												on ? "border-primary bg-primary text-primary-content" : "border-base-content/30 bg-base-100",
											)}
										>
											{on && <Check className="size-[0.75rem]" />}
										</span>
										<span className="min-w-0 flex-1 truncate">{c.header}</span>
										{c.lockVisible && (
											<span className="text-[0.625rem] text-base-content/45 uppercase">{messages.columnAlways}</span>
										)}
									</button>
								</li>
							);
						})}
					</ul>
					{hiddenCount > 0 && (
						<div className="border-t border-base-300 p-[0.25rem]">
							<button
								type="button"
								onClick={() => visibility.onChange(new Set())}
								className="flex w-full items-center gap-[0.5rem] rounded-[0.375rem] px-[0.5rem] py-[0.375rem] text-left text-[0.8125rem] text-base-content/70 hover:bg-base-200"
							>
								<RotateCcw className="size-[0.875rem]" />
								{messages.showAllColumns}
							</button>
						</div>
					)}
				</div>
			)}
		</div>
	);
}
